import {
  Banner,
  Button,
  Card,
  DatePicker,
  Form,
  List,
  LoadingSkeleton,
  popSuccessToast,
  Select,
  ToggleRadio
} from '@boltenergy-be/design-system'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { useTranslation } from 'react-i18next'
import { useForm, useWatch, FormProvider } from 'react-hook-form'
import { NewDiscountInputs } from 'pages/app/tariffs/discounts/types.ts'
import { DIRECTIONS, ENERGY_TYPES, DISCOUNT_REASONS, GetDiscountsResponseBody } from '@boltenergy-be/shared'
import styles from './DiscountsForm.module.scss'
import dayjs from 'dayjs'
import { ISO8601_DATE_FORMAT } from 'constants/constants.ts'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { isInactiveContract } from 'utils/contracts.ts'
import ControlledMultiSelect from 'components/form-fields/multi-select/ControlledMultiSelect.tsx'
import { useGetDiscountsQuery } from 'store/queries/bolt-backend/discounts'
import { useMemo, useState } from 'react'
import backendLib from 'lib/bolt-backend'
import { boltBackend } from 'store/queries/bolt-backend'
import { BOLT_BACKEND_TAGS } from 'store/queries/bolt-backend/constants.ts'
import { useDispatch } from 'react-redux'
import { convertArrayToMap } from 'utils/objects.ts'

const DiscountsForm = () => {
  // Redux
  const { electricity, gas } = useStoreSelector(selectCurrentServiceContracts)
  const onlyElectricityAvailable = !gas || (gas && isInactiveContract(gas))
  const dispatch = useDispatch()

  // Redux queries
  const { data, isLoading, isError } = useGetDiscountsQuery()

  const promotions = useMemo(
    () => convertArrayToMap<GetDiscountsResponseBody['salesforcePromotions'][number]>(data?.salesforcePromotions, (promo) => promo.id),
    [data?.salesforcePromotions]
  )

  const [mostUsedPromotions, otherPromotions] = useMemo(() => {
    const promotionsList = Array.from(promotions?.values() ?? [])

    return [promotionsList.filter((promo) => promo.mostUsed), promotionsList.filter((promo) => !promo.mostUsed)]
  }, [promotions])

  // i18n
  const { t: tDiscounts } = useTranslation('tariffs', { keyPrefix: 'discounts' })
  const { t } = useTranslation(['validation', 'common', 'errors'])
  const language = useGetLanguage()

  // React Hook Form
  const hookForm = useForm<NewDiscountInputs>({
    defaultValues: {
      energyType: ENERGY_TYPES.electricity,
      direction: DIRECTIONS.consumption,
      startDate: dayjs().format(ISO8601_DATE_FORMAT)
    }
  })
  const { control, register, formState, setValue, handleSubmit } = hookForm
  const watchStartDate = useWatch({ control, name: 'startDate' })
  const watchCode = useWatch({ control, name: 'code' })
  const watchEnergyType = useWatch({ control, name: 'energyType' })

  // Local state
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [failedDiscounts, setFailedDiscounts] = useState<string[]>([])
  const [submittedDiscounts, setSubmittedDiscounts] = useState<string[]>([])

  /**
   * Handles the creation of a new discount for the current contract
   * @param {NewDiscountInputs} inputs
   */
  const submitNewDiscount = async (inputs: NewDiscountInputs) => {
    setLoading(true)
    setError(false)
    setFailedDiscounts([])
    setSubmittedDiscounts(inputs.linkedDiscounts)

    try {
      const salesforcePromoCode = promotions?.get(inputs.code)?.salesforcePromoCode

      if (!salesforcePromoCode) {
        throw new Error('salesforcePromoCode is missing')
      }

      const res = await backendLib.applyDiscount({
        contractNumber: inputs.energyType === ENERGY_TYPES.gas && gas ? gas.contractNumber : electricity.contractNumber,
        inputs: {
          ...inputs,
          code: salesforcePromoCode
        }
      })

      if (res.success) {
        popSuccessToast(tDiscounts(($) => $.new.successMessage))
        dispatch(boltBackend.util.invalidateTags([BOLT_BACKEND_TAGS.discounts]))
        hookForm.reset()
      } else {
        setError(true)
        setFailedDiscounts(res.notSuccessfulDiscounts || [])
      }
    } catch (err) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  if (isLoading) {
    return (
      <LoadingSkeleton>
        <LoadingSkeleton.Rectangle width="100%" height={56} />
      </LoadingSkeleton>
    )
  }

  if (!promotions?.size || !data?.haugazelDiscounts?.length || isError) {
    return (
      <Banner
        type="blocking"
        title={t(($) => $.default, {
          ns: 'errors'
        })}
      />
    )
  }

  return (
    <FormProvider {...hookForm}>
      <Form onSubmit={handleSubmit(submitNewDiscount)}>
        <Select {...register('code', { required: t(($) => $.required) })} grid="full" label={tDiscounts(($) => $.new.form.code.label)}>
          <option value="">{tDiscounts(($) => $.new.form.selectPlaceholder)}</option>
          {!!mostUsedPromotions.length && (
            <optgroup label={tDiscounts(($) => $.new.form.code.mostUsed)}>
              {mostUsedPromotions.map(({ id, salesforcePromoCode }) => (
                <option key={id} value={id}>
                  {salesforcePromoCode}
                </option>
              ))}
            </optgroup>
          )}
          {!!otherPromotions.length && (
            <optgroup label={tDiscounts(($) => $.new.form.code.other)}>
              {otherPromotions.map(({ id, salesforcePromoCode }) => (
                <option key={id} value={id}>
                  {salesforcePromoCode}
                </option>
              ))}
            </optgroup>
          )}
        </Select>

        {!!watchCode && (
          <>
            <Card color="neutral" className={styles['code-info']}>
              <Card.Title as="h2" variant="h6" serif={false} weight="semi-bold">
                {tDiscounts(($) => $.new.form.code.info)}
              </Card.Title>

              {promotions.has(watchCode) && <p>{promotions.get(watchCode)?.promoText[language]}</p>}
            </Card>

            {!onlyElectricityAvailable && (
              <ToggleRadio
                grid="full"
                name="energyType"
                label={tDiscounts(($) => $.new.form.energyType.label)}
                error={formState?.errors?.energyType?.message}
                options={[
                  {
                    ...register('energyType'),
                    value: ENERGY_TYPES.electricity,
                    id: ENERGY_TYPES.electricity,
                    label: t(($) => $.electricity, {
                      ns: 'common'
                    })
                  },
                  {
                    ...register('energyType', {
                      onChange: () => {
                        setValue('direction', DIRECTIONS.consumption)
                      }
                    }),
                    value: ENERGY_TYPES.gas,
                    id: ENERGY_TYPES.gas,
                    label: t(($) => $.gas, {
                      ns: 'common'
                    })
                  }
                ]}
              />
            )}

            <ToggleRadio
              grid="full"
              name="direction"
              label={tDiscounts(($) => $.new.form.direction.label)}
              disabled={watchEnergyType === ENERGY_TYPES.gas}
              error={formState?.errors?.direction?.message}
              options={[
                {
                  ...register('direction'),
                  value: DIRECTIONS.consumption,
                  id: DIRECTIONS.consumption,
                  label: tDiscounts(($) => $.new.form.direction.options.consumption)
                },
                {
                  ...register('direction'),
                  value: DIRECTIONS.production,
                  id: DIRECTIONS.production,
                  label: tDiscounts(($) => $.new.form.direction.options.production)
                }
              ]}
            />

            <ControlledMultiSelect
              grid="full"
              identifier="linkedDiscounts"
              label={tDiscounts(($) => $.new.form.linkedDiscounts.label)}
              options={data?.haugazelDiscounts.map((promo) => ({
                label: promo.description,
                value: promo.id
              }))}
            />

            <Select
              {...register('reason', { required: t(($) => $.required) })}
              label={tDiscounts(($) => $.new.form.reason.label)}
              error={formState?.errors?.reason?.message}
              layoutClassName={styles.reason}
            >
              <option value="">{tDiscounts(($) => $.new.form.selectPlaceholder)}</option>
              {Object.values(DISCOUNT_REASONS).map((reason) => (
                <option key={reason} value={reason}>
                  {tDiscounts(($) => $.new.form.reason.options[reason])}
                </option>
              ))}
            </Select>

            <DatePicker
              {...{ language }}
              label={tDiscounts(($) => $.new.form.startDate.label)}
              dateFormat="dd/MM/yyyy"
              portalId="cal-root"
              name="startDate"
              wrapperClassName={styles.calender}
              error={formState.errors?.startDate?.message}
              selected={watchStartDate ? dayjs(watchStartDate).toDate() : undefined}
              onChange={(date) => {
                if (typeof date === 'string' || typeof date === 'object') {
                  const isValidDate = dayjs(date as string).isValid()

                  if (isValidDate) {
                    setValue('startDate', dayjs(date as string).format(ISO8601_DATE_FORMAT), { shouldValidate: true })
                  }
                }
              }}
              suffix={{ as: 'calendar' }}
              minDate={dayjs(
                watchEnergyType === ENERGY_TYPES.gas && !!gas ? gas.detail.contractualStartDate : electricity.detail.contractualStartDate
              ).toDate()}
            />

            <Form.Footer>
              <Button loading={loading}>{tDiscounts(($) => $.new.form.submit)}</Button>
            </Form.Footer>
          </>
        )}

        {error && (
          <Banner
            className="grid-col-full"
            type={failedDiscounts.length < submittedDiscounts.length ? 'warning' : 'blocking'}
            title={tDiscounts(($) => $.new.errorMessage, {
              context:
                submittedDiscounts.length === failedDiscounts.length && submittedDiscounts.length > 1
                  ? 'all'
                  : failedDiscounts.length
                    ? 'specified'
                    : 'unknown'
            })}
          >
            {!!failedDiscounts.length && (
              <List as="ul">
                {failedDiscounts.map((discount) => (
                  <li key={discount}>{discount}</li>
                ))}
              </List>
            )}
          </Banner>
        )}
      </Form>
    </FormProvider>
  )
}

export default DiscountsForm
